import { useState, useEffect } from "react";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Search, Eye, CheckCircle, XCircle, Clock, ArrowUp, Filter, Users, FileText, MessageSquare, UserCheck } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { DataTable } from "@/components/data-table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import ApprovalWorkflowsService from "@/api/approvalWorkflows.service";
import UsersService from "@/api/users.service";

interface ApprovalRequest {
  id: number;
  publicId: string;
  businessId: number;
  approvalWorkflowId: number;
  documentType: string;
  documentId: number;
  requestedBy: number;
  currentStepId?: number;
  status: 'pending' | 'approved' | 'rejected' | 'escalated';
  totalAmount?: number;
  currency?: string;
  requestData?: any;
  dateCreated: string;
  workflow?: {
    id: number;
    name: string;
  };
  requesterUser?: {
    id: number;
    firstName: string;
    lastName: string;
    email: string;
  };
  currentStep?: {
    id: number;
    stepName: string;
    stepOrder: number;
    approverType: string;
    requiredApprovals: number;
  };
  actions?: ApprovalAction[];
}

interface ApprovalAction {
  id: number;
  publicId: string;
  approvalRequestId: number;
  approvalStepId: number;
  approverUserId: number;
  action: 'approved' | 'rejected' | 'escalated' | 'delegated';
  comments?: string;
  digitalSignature?: string;
  ipAddress?: string;
  deviceInfo?: string;
  dateCreated: string;
  approverUser?: {
    id: number;
    firstName: string;
    lastName: string;
    email: string;
  };
}

interface User {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  role: string;
}

export default function ApprovalRequests() {
  const [requests, setRequests] = useState<ApprovalRequest[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [documentTypeFilter, setDocumentTypeFilter] = useState<string>("all");
  const [selectedRequest, setSelectedRequest] = useState<ApprovalRequest | null>(null);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [isApproveDialogOpen, setIsApproveDialogOpen] = useState(false);
  const [isRejectDialogOpen, setIsRejectDialogOpen] = useState(false);
  const [isEscalateDialogOpen, setIsEscalateDialogOpen] = useState(false);
  const [isDelegateDialogOpen, setIsDelegateDialogOpen] = useState(false);
  const [currentTab, setCurrentTab] = useState("pending");

  // Form state
  const [actionComments, setActionComments] = useState("");
  const [delegateToUserId, setDelegateToUserId] = useState("");
  const [escalateToUserId, setEscalateToUserId] = useState("");

  const { toast } = useToast();

  const [selectedBusiness] = useState(() => {
    const storedBusiness = typeof window !== 'undefined' ? window.localStorage.getItem('selectedBusiness') : null;
    return storedBusiness ? JSON.parse(storedBusiness) : null;
  });

  const [currentUser] = useState(() => {
    const storedUser = typeof window !== 'undefined' ? window.localStorage.getItem('user') : null;
    return storedUser ? JSON.parse(storedUser) : null;
  });

  useEffect(() => {
    if (selectedBusiness) {
      fetchData();
      fetchUsers();
    }
  }, [selectedBusiness]);

  const fetchData = async () => {
    if (!selectedBusiness) return;
    
    try {
      setLoading(true);
      const response = await ApprovalWorkflowsService.getApprovalRequests(selectedBusiness.id);
      if (response.data.result === "success") {
        setRequests(response.data.requests || []);
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to fetch approval requests",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await UsersService.getAllUsers();
      if (response.data.result === "success") {
        setUsers(response.data.users || []);
      }
    } catch (error) {
      console.error("Failed to fetch users:", error);
    }
  };

  const handleApprove = async () => {
    if (!selectedRequest || !currentUser) return;

    try {
      const actionData = {
        approvalRequestId: selectedRequest.id,
        approvalStepId: selectedRequest.currentStepId,
        approverUserId: currentUser.id,
        action: 'approved',
        comments: actionComments
      };

      await ApprovalWorkflowsService.processApprovalAction(actionData);
      toast({
        title: "Success",
        description: "Request approved successfully",
      });
      setIsApproveDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to approve request",
        variant: "destructive",
      });
    }
  };

  const handleReject = async () => {
    if (!selectedRequest || !currentUser) return;

    try {
      const actionData = {
        approvalRequestId: selectedRequest.id,
        approvalStepId: selectedRequest.currentStepId,
        approverUserId: currentUser.id,
        action: 'rejected',
        comments: actionComments
      };

      await ApprovalWorkflowsService.processApprovalAction(actionData);
      toast({
        title: "Success",
        description: "Request rejected successfully",
      });
      setIsRejectDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to reject request",
        variant: "destructive",
      });
    }
  };

  const handleEscalate = async () => {
    if (!selectedRequest || !currentUser || !escalateToUserId) return;

    try {
      const actionData = {
        approvalRequestId: selectedRequest.id,
        approvalStepId: selectedRequest.currentStepId,
        approverUserId: currentUser.id,
        action: 'escalated',
        comments: actionComments,
        escalateToUserId: parseInt(escalateToUserId)
      };

      await ApprovalWorkflowsService.processApprovalAction(actionData);
      toast({
        title: "Success",
        description: "Request escalated successfully",
      });
      setIsEscalateDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to escalate request",
        variant: "destructive",
      });
    }
  };

  const handleDelegate = async () => {
    if (!selectedRequest || !currentUser || !delegateToUserId) return;

    try {
      const actionData = {
        approvalRequestId: selectedRequest.id,
        approvalStepId: selectedRequest.currentStepId,
        approverUserId: currentUser.id,
        action: 'delegated',
        comments: actionComments,
        delegateToUserId: parseInt(delegateToUserId)
      };

      await ApprovalWorkflowsService.processApprovalAction(actionData);
      toast({
        title: "Success",
        description: "Request delegated successfully",
      });
      setIsDelegateDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delegate request",
        variant: "destructive",
      });
    }
  };

  const resetForm = () => {
    setActionComments("");
    setDelegateToUserId("");
    setEscalateToUserId("");
  };

  const openViewDialog = (request: ApprovalRequest) => {
    setSelectedRequest(request);
    setIsViewDialogOpen(true);
  };

  const openApproveDialog = (request: ApprovalRequest) => {
    setSelectedRequest(request);
    setIsApproveDialogOpen(true);
  };

  const openRejectDialog = (request: ApprovalRequest) => {
    setSelectedRequest(request);
    setIsRejectDialogOpen(true);
  };

  const openEscalateDialog = (request: ApprovalRequest) => {
    setSelectedRequest(request);
    setIsEscalateDialogOpen(true);
  };

  const openDelegateDialog = (request: ApprovalRequest) => {
    setSelectedRequest(request);
    setIsDelegateDialogOpen(true);
  };

  const getStatusBadge = (status: string) => {
    const variants = {
      approved: "bg-green-50 text-green-700 border-green-200",
      pending: "bg-amber-50 text-amber-700 border-amber-200",
      rejected: "bg-red-50 text-red-700 border-red-200",
      escalated: "bg-purple-50 text-purple-700 border-purple-200"
    };

    const icons = {
      approved: <CheckCircle className="h-4 w-4" />,
      pending: <Clock className="h-4 w-4" />,
      rejected: <XCircle className="h-4 w-4" />,
      escalated: <ArrowUp className="h-4 w-4" />
    };

    return (
      <Badge className={variants[status as keyof typeof variants] || variants.pending}>
        {icons[status as keyof typeof icons] || icons.pending}
        <span className="ml-1 capitalize">{status}</span>
      </Badge>
    );
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount: number, currency: string = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };

  const filteredRequests = requests.filter(request => {
    const matchesSearch = request.documentType.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         request.workflow?.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         request.requesterUser?.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         request.requesterUser?.lastName.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === "all" || request.status === statusFilter;
    const matchesDocumentType = documentTypeFilter === "all" || request.documentType === documentTypeFilter;
    const matchesTab = currentTab === "all" || 
                      (currentTab === "pending" && request.status === 'pending') ||
                      (currentTab === "my-requests" && request.requestedBy === currentUser?.id) ||
                      (currentTab === "assigned" && request.currentStep?.approverType === 'user');
    
    return matchesSearch && matchesStatus && matchesDocumentType && matchesTab;
  });

  const requestColumns = [
    {
      accessorKey: "documentType",
      header: "Document",
      cell: ({ row }: any) => (
        <div className="flex items-center gap-2">
          <FileText className="h-4 w-4 text-muted-foreground" />
          <div>
            <div className="font-medium capitalize">{row.getValue("documentType").replace('_', ' ')}</div>
            <div className="text-sm text-muted-foreground">
              ID: {row.original.documentId}
            </div>
          </div>
        </div>
      ),
    },
    {
      accessorKey: "workflow",
      header: "Workflow",
      cell: ({ row }: any) => {
        const workflow = row.getValue("workflow");
        return workflow ? (
          <div className="text-sm">
            <div className="font-medium">{workflow.name}</div>
            <div className="text-muted-foreground">
              Step: {row.original.currentStep?.stepName || 'N/A'}
            </div>
          </div>
        ) : (
          <span className="text-muted-foreground">No workflow</span>
        );
      },
    },
    {
      accessorKey: "totalAmount",
      header: "Amount",
      cell: ({ row }: any) => {
        const amount = row.getValue("totalAmount");
        const currency = row.original.currency || 'USD';
        return amount ? formatCurrency(amount, currency) : 'N/A';
      },
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ row }: any) => getStatusBadge(row.getValue("status")),
    },
    {
      accessorKey: "requestedBy",
      header: "Requested By",
      cell: ({ row }: any) => {
        const requester = row.original.requesterUser;
        return requester ? (
          <div className="text-sm">
            {requester.firstName} {requester.lastName}
          </div>
        ) : (
          <span className="text-muted-foreground">Unknown</span>
        );
      },
    },
    {
      accessorKey: "dateCreated",
      header: "Created",
      cell: ({ row }: any) => formatDate(row.getValue("dateCreated")),
    },
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }: any) => {
        const request = row.original;
        const canApprove = request.status === 'pending' && currentUser;
        
        return (
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => openViewDialog(request)}
            >
              <Eye className="h-4 w-4" />
            </Button>
            {canApprove && (
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => openApproveDialog(request)}
                  className="text-green-600 hover:text-green-700"
                >
                  <CheckCircle className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => openRejectDialog(request)}
                  className="text-red-600 hover:text-red-700"
                >
                  <XCircle className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => openEscalateDialog(request)}
                  className="text-purple-600 hover:text-purple-700"
                >
                  <ArrowUp className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => openDelegateDialog(request)}
                  className="text-blue-600 hover:text-blue-700"
                >
                  <UserCheck className="h-4 w-4" />
                </Button>
              </>
            )}
          </div>
        );
      },
    },
  ];

  const statusCounts = {
    all: requests.length,
    pending: requests.filter(r => r.status === 'pending').length,
    approved: requests.filter(r => r.status === 'approved').length,
    rejected: requests.filter(r => r.status === 'rejected').length,
    escalated: requests.filter(r => r.status === 'escalated').length,
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Approval Requests</h1>
          <p className="text-muted-foreground">
            Manage and process document approval requests
          </p>
        </div>
      </div>

      {/* Status Overview Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {Object.entries(statusCounts).map(([status, count]) => (
          <Card key={status} className="cursor-pointer hover:shadow-md transition-shadow"
                onClick={() => setStatusFilter(status)}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground capitalize">
                    {status === 'all' ? 'Total' : status}
                  </p>
                  <p className="text-2xl font-bold">{count}</p>
                </div>
                {status !== 'all' && getStatusBadge(status).props.children[0]}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Tabs */}
      <Tabs value={currentTab} onValueChange={setCurrentTab} className="w-full">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="pending">Pending</TabsTrigger>
          <TabsTrigger value="my-requests">My Requests</TabsTrigger>
          <TabsTrigger value="assigned">Assigned to Me</TabsTrigger>
          <TabsTrigger value="all">All Requests</TabsTrigger>
        </TabsList>

        <TabsContent value={currentTab} className="space-y-6">
          {/* Filters and Search */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Filter className="h-5 w-5" />
                Filters & Search
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="search">Search</Label>
                  <div className="relative">
                    <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="search"
                      placeholder="Search requests..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-9"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="status-filter">Status</Label>
                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger>
                      <SelectValue placeholder="All statuses" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Statuses</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="approved">Approved</SelectItem>
                      <SelectItem value="rejected">Rejected</SelectItem>
                      <SelectItem value="escalated">Escalated</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="document-type-filter">Document Type</Label>
                  <Select value={documentTypeFilter} onValueChange={setDocumentTypeFilter}>
                    <SelectTrigger>
                      <SelectValue placeholder="All types" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Types</SelectItem>
                      <SelectItem value="invoice">Invoice</SelectItem>
                      <SelectItem value="expense">Expense</SelectItem>
                      <SelectItem value="bill">Bill</SelectItem>
                      <SelectItem value="purchase_order">Purchase Order</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Requests Table */}
          <Card>
            <CardHeader>
              <CardTitle>Approval Requests ({filteredRequests.length})</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin" />
                </div>
              ) : (
                <DataTable
                  columns={requestColumns}
                  data={filteredRequests}
                  section="approval-requests"
                  isLoading={loading}
                />
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* View Request Dialog */}
      <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Request Details</DialogTitle>
            <DialogDescription>
              View approval request information and history
            </DialogDescription>
          </DialogHeader>
          {selectedRequest && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Document Type</Label>
                  <p className="text-sm capitalize">{selectedRequest.documentType.replace('_', ' ')}</p>
                </div>
                <div>
                  <Label>Document ID</Label>
                  <p className="text-sm">{selectedRequest.documentId}</p>
                </div>
                <div>
                  <Label>Workflow</Label>
                  <p className="text-sm">{selectedRequest.workflow?.name || 'N/A'}</p>
                </div>
                <div>
                  <Label>Current Step</Label>
                  <p className="text-sm">{selectedRequest.currentStep?.stepName || 'N/A'}</p>
                </div>
                <div>
                  <Label>Amount</Label>
                  <p className="text-sm">
                    {selectedRequest.totalAmount 
                      ? formatCurrency(selectedRequest.totalAmount, selectedRequest.currency)
                      : 'N/A'
                    }
                  </p>
                </div>
                <div>
                  <Label>Status</Label>
                  <div className="mt-1">
                    {getStatusBadge(selectedRequest.status)}
                  </div>
                </div>
                <div>
                  <Label>Requested By</Label>
                  <p className="text-sm">
                    {selectedRequest.requesterUser 
                      ? `${selectedRequest.requesterUser.firstName} ${selectedRequest.requesterUser.lastName}`
                      : 'Unknown'
                    }
                  </p>
                </div>
                <div>
                  <Label>Created</Label>
                  <p className="text-sm">{formatDate(selectedRequest.dateCreated)}</p>
                </div>
              </div>

              {selectedRequest.actions && selectedRequest.actions.length > 0 && (
                <div>
                  <Label>Approval History</Label>
                  <div className="mt-2 space-y-3">
                    {selectedRequest.actions.map((action, index) => (
                      <Card key={action.id}>
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-medium">
                                {index + 1}
                              </div>
                              <div>
                                <h4 className="font-medium capitalize">{action.action}</h4>
                                <p className="text-sm text-muted-foreground">
                                  by {action.approverUser?.firstName} {action.approverUser?.lastName}
                                </p>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="text-sm">{formatDate(action.dateCreated)}</p>
                            </div>
                          </div>
                          {action.comments && (
                            <div className="mt-3 p-3 bg-muted rounded-md">
                              <p className="text-sm">{action.comments}</p>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Approve Request Dialog */}
      <Dialog open={isApproveDialogOpen} onOpenChange={setIsApproveDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Approve Request</DialogTitle>
            <DialogDescription>
              Approve this document request
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="approve-comments">Comments (Optional)</Label>
              <Textarea
                id="approve-comments"
                value={actionComments}
                onChange={(e) => setActionComments(e.target.value)}
                placeholder="Add any comments about your approval..."
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setIsApproveDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleApprove} className="bg-green-600 hover:bg-green-700">
                <CheckCircle className="h-4 w-4 mr-2" />
                Approve
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reject Request Dialog */}
      <Dialog open={isRejectDialogOpen} onOpenChange={setIsRejectDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Request</DialogTitle>
            <DialogDescription>
              Reject this document request
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="reject-comments">Reason for Rejection</Label>
              <Textarea
                id="reject-comments"
                value={actionComments}
                onChange={(e) => setActionComments(e.target.value)}
                placeholder="Please provide a reason for rejection..."
                required
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setIsRejectDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleReject} 
                className="bg-red-600 hover:bg-red-700"
                disabled={!actionComments.trim()}
              >
                <XCircle className="h-4 w-4 mr-2" />
                Reject
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Escalate Request Dialog */}
      <Dialog open={isEscalateDialogOpen} onOpenChange={setIsEscalateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Escalate Request</DialogTitle>
            <DialogDescription>
              Escalate this request to a higher authority
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="escalate-to">Escalate To</Label>
              <Select value={escalateToUserId} onValueChange={setEscalateToUserId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select user to escalate to" />
                </SelectTrigger>
                <SelectContent>
                  {users.map((user) => (
                    <SelectItem key={user.id} value={user.id.toString()}>
                      {user.firstName} {user.lastName} ({user.role})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="escalate-comments">Escalation Reason</Label>
              <Textarea
                id="escalate-comments"
                value={actionComments}
                onChange={(e) => setActionComments(e.target.value)}
                placeholder="Please provide a reason for escalation..."
                required
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setIsEscalateDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleEscalate}
                disabled={!escalateToUserId || !actionComments.trim()}
                className="bg-purple-600 hover:bg-purple-700"
              >
                <ArrowUp className="h-4 w-4 mr-2" />
                Escalate
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delegate Request Dialog */}
      <Dialog open={isDelegateDialogOpen} onOpenChange={setIsDelegateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delegate Request</DialogTitle>
            <DialogDescription>
              Delegate this approval to another user
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="delegate-to">Delegate To</Label>
              <Select value={delegateToUserId} onValueChange={setDelegateToUserId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select user to delegate to" />
                </SelectTrigger>
                <SelectContent>
                  {users.map((user) => (
                    <SelectItem key={user.id} value={user.id.toString()}>
                      {user.firstName} {user.lastName} ({user.role})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="delegate-comments">Delegation Notes (Optional)</Label>
              <Textarea
                id="delegate-comments"
                value={actionComments}
                onChange={(e) => setActionComments(e.target.value)}
                placeholder="Add any notes for the delegate..."
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setIsDelegateDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleDelegate}
                disabled={!delegateToUserId}
                className="bg-blue-600 hover:bg-blue-700"
              >
                <UserCheck className="h-4 w-4 mr-2" />
                Delegate
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
} 